(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TAU = Math.PI * 2;
  const DEFAULT_ROLL = { duration: 1800, rotations: 4 };
  const HOVER_RETURN = { duration: 860, turns: 1, yArc: 0.28, zArc: 0.12 };
  const IDLE_TUMBLE = { delay: 5000, stagger: 120 };
  const HOVER_MAGNET = { xRange: 0.26, yRange: 0.3, zRange: 0.08, easing: 0.16 };
  const ENTRY_ROLL = { baseDuration: 1500, delayStep: 150, rotations: 3.8 };
  // Halloween dark mode recolours every die to one of these looks; without the setting the block colours stand.
  const HALLOWEEN_DICE = {
    ghostly: { dice: 'rgba(214, 236, 230, 0.3)', pips: 'rgba(240, 252, 248, 0.95)' },
    radioactive: { dice: '#9be53a', pips: '#14200c' },
  };
  let zdogLoadPromise = null;
  const FACE_ROTATIONS = {
    1: { x: 0.36, y: -0.36, z: -0.03 },
    2: { x: 1.34, y: -0.42, z: 0.08 },
    3: { x: 0.42, y: 1.34, z: 0.08 },
    4: { x: 0.42, y: -1.34, z: -0.08 },
    5: { x: -1.34, y: -0.42, z: -0.08 },
    6: { x: 0.36, y: 2.78, z: 0.03 },
  };

  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const loadZdog = (src) => {
    if (window.Zdog) return Promise.resolve(window.Zdog);
    if (!src) return Promise.reject(new Error('Missing Zdog asset URL'));
    if (zdogLoadPromise) return zdogLoadPromise;

    zdogLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve(window.Zdog);
      script.onerror = () => reject(new Error('Unable to load Zdog'));
      document.head.appendChild(script);
    });

    return zdogLoadPromise;
  };

  class ZdogDie {
    constructor(canvas) {
      this.canvas = canvas;
      this.face = Number(canvas.dataset.playerCount) || 1;
      this.diceColor = canvas.dataset.diceColor || '#d10f13';
      this.pipColor = canvas.dataset.pipColor || '#ffffff';
      const halloweenDice = HALLOWEEN_DICE[document.documentElement.dataset.hdmDice];
      if (halloweenDice) {
        this.diceColor = halloweenDice.dice;
        this.pipColor = halloweenDice.pips;
      }
      this.rolling = false;
      this.hovering = false;
      this.returning = false;
      this.startedAt = 0;
      this.hoverStartedAt = 0;
      this.returnStartedAt = 0;
      this.duration = DEFAULT_ROLL.duration;
      this.rotations = DEFAULT_ROLL.rotations;
      this.targetRotation = FACE_ROTATIONS[this.face] || FACE_ROTATIONS[1];
      this.restRotation = { ...this.targetRotation };
      this.startRotation = this.targetRotation;
      this.returnRotation = this.targetRotation;
      this.returnTargetRotation = { ...this.targetRotation };
      this.returnDirection = { x: 1, y: 1, z: 1 };
      this.hoverRotation = { ...this.targetRotation };
      this.hoverTargetRotation = { ...this.targetRotation };
      this.illo = new Zdog.Illustration({
        element: canvas,
        zoom: 2.95,
      });
      this.die = new Zdog.Anchor({
        addTo: this.illo,
        rotate: this.targetRotation,
      });

      this.buildDie();
      this.setRotation(this.targetRotation);
      this.render();
    }

    buildDie() {
      const faces = new Zdog.Group({ addTo: this.die });
      const base = new Zdog.Rect({
        addTo: faces,
        width: 50,
        height: 50,
        stroke: 50,
        color: this.diceColor,
        fill: true,
        translate: { z: -25 },
      });

      base.copy({
        rotate: { x: TAU / 4 },
        translate: { y: 25 },
      });
      base.copy({
        rotate: { x: TAU / 4 },
        translate: { y: -25 },
      });
      base.copy({
        translate: { z: 25 },
      });

      this.addPips();
    }

    addPips() {
      this.addPipFace(1, { translate: { z: 50 } });
      this.addPipFace(6, { translate: { z: -50 }, rotate: { y: Math.PI } });
      this.addPipFace(3, { translate: { x: 50 }, rotate: { y: TAU / 4 } });
      this.addPipFace(4, { translate: { x: -50 }, rotate: { y: -TAU / 4 } });
      this.addPipFace(2, { translate: { y: 50 }, rotate: { x: TAU / 4 } });
      this.addPipFace(5, { translate: { y: -50 }, rotate: { x: -TAU / 4 } });
    }

    addPipFace(face, transform) {
      const group = new Zdog.Group({
        addTo: this.die,
        rotate: transform.rotate,
        translate: transform.translate,
      });
      const positions = {
        1: [[0, 0]],
        2: [[-18, -18], [18, 18]],
        3: [[-18, -18], [0, 0], [18, 18]],
        4: [[-18, -18], [18, -18], [-18, 18], [18, 18]],
        5: [[-18, -18], [18, -18], [0, 0], [-18, 18], [18, 18]],
        6: [[-18, -21], [18, -21], [-18, 0], [18, 0], [-18, 21], [18, 21]],
      };

      positions[face].forEach(([x, y]) => {
        new Zdog.Ellipse({
          addTo: group,
          diameter: 15,
          stroke: false,
          fill: true,
          color: this.pipColor,
          translate: { x, y, z: 0 },
        });
      });
    }

    roll(animate = true, options = {}) {
      this.rolling = animate && !prefersReducedMotion.matches;
      this.startedAt = performance.now();
      this.duration = options.duration || DEFAULT_ROLL.duration;
      this.rotations = options.rotations || DEFAULT_ROLL.rotations;
      this.startRotation = {
        x: this.targetRotation.x + 0.7 + this.face * 0.12,
        y: this.targetRotation.y - 0.9 - this.face * 0.1,
        z: this.targetRotation.z - 0.36 + this.face * 0.06,
      };

      if (!this.rolling) {
        this.restRotation = { ...this.targetRotation };
        this.setRotation(this.targetRotation);
        this.render();
      }
    }

    update(now) {
      if (!this.rolling && !this.hovering && !this.returning) return false;

      if (this.hovering) {
        this.hoverRotation.x = lerp(this.hoverRotation.x, this.hoverTargetRotation.x, HOVER_MAGNET.easing);
        this.hoverRotation.y = lerp(this.hoverRotation.y, this.hoverTargetRotation.y, HOVER_MAGNET.easing);
        this.hoverRotation.z = lerp(this.hoverRotation.z, this.hoverTargetRotation.z, HOVER_MAGNET.easing);
        this.setRotation(this.hoverRotation);
        this.render();
        return true;
      }

      if (this.returning) {
        const elapsed = now - this.returnStartedAt;
        const rawProgress = Math.min(elapsed / HOVER_RETURN.duration, 1);
        const eased = easeOutCubic(rawProgress);
        const arc = Math.sin(rawProgress * Math.PI);

        this.die.rotate.x = lerp(this.returnRotation.x, this.returnTargetRotation.x, eased);
        this.die.rotate.y = lerp(this.returnRotation.y, this.returnTargetRotation.y, eased) + arc * HOVER_RETURN.yArc * this.returnDirection.y;
        this.die.rotate.z = lerp(this.returnRotation.z, this.returnTargetRotation.z, eased) + arc * HOVER_RETURN.zArc * this.returnDirection.z;
        this.render();

        if (rawProgress >= 1) {
          this.returning = false;
          this.restRotation = { ...this.returnTargetRotation };
          this.setRotation(this.restRotation);
          this.render();
        }

        return this.returning;
      }

      const elapsed = now - this.startedAt;
      const rawProgress = Math.min(elapsed / this.duration, 1);
      const eased = easeOutCubic(rawProgress);
      const spin = (1 - rawProgress) * TAU * this.rotations;

      this.die.rotate.x = lerp(this.startRotation.x + spin, this.targetRotation.x, eased);
      this.die.rotate.y = lerp(this.startRotation.y - spin, this.targetRotation.y, eased);
      this.die.rotate.z = lerp(this.startRotation.z, this.targetRotation.z, eased);
      this.render();

      if (rawProgress >= 1) {
        this.rolling = false;
        this.setRotation(this.targetRotation);
        this.restRotation = { ...this.targetRotation };
        this.render();
      }

      return this.rolling;
    }

    startHover(now) {
      if (prefersReducedMotion.matches) return;

      this.rolling = false;
      this.returning = false;
      this.hovering = true;
      this.hoverStartedAt = now;
      this.hoverRotation = {
        x: this.die.rotate.x,
        y: this.die.rotate.y,
        z: this.die.rotate.z,
      };
      this.hoverTargetRotation = { ...this.hoverRotation };
    }

    updateHoverTarget(xPercent = 0, yPercent = 0) {
      if (!this.hovering) return;

      this.hoverTargetRotation = {
        x: this.restRotation.x - yPercent * HOVER_MAGNET.xRange,
        y: this.restRotation.y + xPercent * HOVER_MAGNET.yRange,
        z: this.restRotation.z + xPercent * HOVER_MAGNET.zRange,
      };
    }

    endHover(now) {
      if (!this.hovering && !this.rolling) return false;

      this.rolling = false;
      this.hovering = false;
      this.returning = true;
      this.returnStartedAt = now;
      this.returnRotation = {
        x: this.die.rotate.x,
        y: this.die.rotate.y,
        z: this.die.rotate.z,
      };
      this.returnDirection = {
        x: this.getReturnDirection('x'),
        y: this.getReturnDirection('y'),
        z: this.getReturnDirection('z'),
      };
      this.returnTargetRotation = {
        x: this.restRotation.x + TAU * HOVER_RETURN.turns * this.returnDirection.x,
        y: this.restRotation.y,
        z: this.restRotation.z,
      };
      return true;
    }

    idleTumble(now) {
      if (this.rolling || this.hovering || this.returning || prefersReducedMotion.matches) return false;

      const direction = Math.random() > 0.5 ? 1 : -1;
      this.returning = true;
      this.returnStartedAt = now;
      this.returnRotation = {
        x: this.die.rotate.x,
        y: this.die.rotate.y,
        z: this.die.rotate.z,
      };
      this.returnDirection = {
        x: direction,
        y: Math.random() > 0.5 ? 1 : -1,
        z: Math.random() > 0.5 ? 1 : -1,
      };
      this.returnTargetRotation = {
        x: this.restRotation.x + TAU * HOVER_RETURN.turns * direction,
        y: this.restRotation.y,
        z: this.restRotation.z,
      };

      return true;
    }

    getReturnDirection(axis) {
      return this.die.rotate[axis] >= this.restRotation[axis] ? 1 : -1;
    }

    setRotation(rotation) {
      this.die.rotate.x = rotation.x;
      this.die.rotate.y = rotation.y;
      this.die.rotate.z = rotation.z;
    }

    render() {
      this.illo.updateRenderGraph();
    }

    settle() {
      this.rolling = false;
      this.hovering = false;
      this.returning = false;
      this.restRotation = { ...this.targetRotation };
      this.setRotation(this.targetRotation);
      this.render();
    }
  }

  class PlayerCountShop extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;

      this.bound = true;
      this.controller = new AbortController();
      this.dice = [];
      this.diceReady = false;
      this.loadingDice = null;
      this.frame = null;
      this.idleTumbleTimers = new Set();
      this.visible = false;
      this.classList.add('is-ready');
      this.shouldAnimate = this.dataset.animate === 'true' && !prefersReducedMotion.matches;
      this.shouldHoverInteract = this.shouldAnimate && window.matchMedia('(hover: hover)').matches;
      this.shouldIdleTumble = this.shouldAnimate && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

      // Re-evaluate on change, otherwise switching the OS setting needs a page reload to take effect.
      this.handleMotionPreferenceChange = () => {
        this.shouldAnimate = this.dataset.animate === 'true' && !prefersReducedMotion.matches;
        this.shouldHoverInteract = this.shouldAnimate && window.matchMedia('(hover: hover)').matches;
        this.shouldIdleTumble = this.shouldAnimate && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        if (!this.shouldAnimate) {
          window.cancelAnimationFrame(this.frame);
          this.frame = null;
          this.clearIdleTumbleTimers();
        }
      };
      prefersReducedMotion.addEventListener('change', this.handleMotionPreferenceChange);

      this.watchVisibility();

      this.handleBlockSelect = (event) => {
        const item = event.target?.closest?.('.player-count-shop__item');
        if (!item || !this.contains(item)) return;

        this.reveal();
        item.querySelector('.player-count-shop__link')?.focus({ preventScroll: true });
      };

      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal: this.controller.signal });

      this.handleDiceHoverStart = (event) => {
        if (!this.shouldHoverInteract) return;

        const canvas = event.currentTarget?.querySelector?.('.player-count-shop__dice-canvas');
        const die = this.dice.find((dice) => dice.canvas === canvas);
        if (!die) return;

        die.startHover(performance.now());
        if (typeof event.clientX === 'number') {
          this.updateDiceMagnet(event.currentTarget, die, event);
        } else {
          die.updateHoverTarget(0.35, -0.25);
        }
        this.startLoop();
      };

      this.handleDiceHoverMove = (event) => {
        if (!this.shouldHoverInteract) return;

        const canvas = event.currentTarget?.querySelector?.('.player-count-shop__dice-canvas');
        const die = this.dice.find((dice) => dice.canvas === canvas);
        if (!die) return;

        this.updateDiceMagnet(event.currentTarget, die, event);
        this.startLoop();
      };

      this.handleDiceHoverEnd = (event) => {
        if (!this.shouldHoverInteract) return;

        const canvas = event.currentTarget?.querySelector?.('.player-count-shop__dice-canvas');
        const die = this.dice.find((dice) => dice.canvas === canvas);
        if (!die) return;

        if (die.endHover(performance.now())) this.startLoop();
      };

      this.querySelectorAll('.player-count-shop__link').forEach((link) => {
        link.addEventListener('pointerenter', this.handleDiceHoverStart, { signal: this.controller.signal });
        link.addEventListener('pointermove', this.handleDiceHoverMove, { signal: this.controller.signal });
        link.addEventListener('pointerleave', this.handleDiceHoverEnd, { signal: this.controller.signal });
        link.addEventListener('focus', this.handleDiceHoverStart, { signal: this.controller.signal });
        link.addEventListener('blur', this.handleDiceHoverEnd, { signal: this.controller.signal });
      });
    }

    disconnectedCallback() {
      if (this.handleMotionPreferenceChange) {
        prefersReducedMotion.removeEventListener('change', this.handleMotionPreferenceChange);
      }
      this.observer?.disconnect();
      this.controller?.abort();
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
      this.clearIdleTumbleTimers();
      this.bound = false;
      this.visible = false;
    }

    async ensureDice() {
      if (this.diceReady) return true;
      if (this.loadingDice) return this.loadingDice;

      this.loadingDice = loadZdog(this.dataset.zdogSrc)
        .then(() => {
          if (!this.isConnected) return false;

          this.dice = Array.from(this.querySelectorAll('.player-count-shop__dice-canvas')).map((canvas) => new ZdogDie(canvas));
          this.diceReady = true;
          return true;
        })
        .catch(() => {
          this.classList.add('is-visible');
          return false;
        });

      return this.loadingDice;
    }

    updateDiceMagnet(link, die, event) {
      const bounds = link.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;

      const xPercent = clamp(((event.clientX - bounds.left) / bounds.width - 0.5) * 2, -1, 1);
      const yPercent = clamp(((event.clientY - bounds.top) / bounds.height - 0.5) * 2, -1, 1);
      die.updateHoverTarget(xPercent, yPercent);
    }

    async reveal() {
      if (this.visible) return;

      this.visible = true;
      this.classList.add('is-visible');
      if (!(await this.ensureDice())) return;
      if (!this.visible) return;

      this.dice.forEach((die) => {
        die.roll(this.shouldAnimate, {
          duration: ENTRY_ROLL.baseDuration + Math.max(die.face - 1, 0) * ENTRY_ROLL.delayStep,
          rotations: ENTRY_ROLL.rotations,
        });
      });
      if (this.shouldAnimate) this.startLoop();
      this.scheduleIdleTumble(ENTRY_ROLL.baseDuration + this.dice.length * ENTRY_ROLL.delayStep + IDLE_TUMBLE.delay);
    }

    conceal() {
      if (!this.visible) return;

      this.visible = false;
      this.classList.remove('is-visible');
      window.cancelAnimationFrame(this.frame);
      this.frame = null;
      this.clearIdleTumbleTimers();
      this.dice.forEach((die) => {
        if (die.rolling || die.hovering || die.returning) die.settle();
      });
    }

    clearIdleTumbleTimers() {
      this.idleTumbleTimers.forEach((timer) => window.clearTimeout(timer));
      this.idleTumbleTimers.clear();
    }

    setIdleTumbleTimer(callback, delay) {
      const timer = window.setTimeout(() => {
        this.idleTumbleTimers.delete(timer);
        callback();
      }, delay);

      this.idleTumbleTimers.add(timer);
    }

    scheduleIdleTumble(delay = IDLE_TUMBLE.delay) {
      this.clearIdleTumbleTimers();
      if (!this.visible || !this.diceReady || !this.shouldIdleTumble) return;

      this.queueIdleTumbleCycle(delay);
    }

    queueIdleTumbleCycle(delay) {
      this.setIdleTumbleTimer(() => {
        if (!this.visible || !this.diceReady || !this.shouldIdleTumble) return;

        this.dice
          .slice()
          .sort((first, second) => first.face - second.face)
          .forEach((die, index) => {
            this.setIdleTumbleTimer(() => {
              if (!this.visible || !this.shouldIdleTumble) return;
              if (die.idleTumble(performance.now())) this.startLoop();
            }, index * IDLE_TUMBLE.stagger);
          });

        this.queueIdleTumbleCycle(IDLE_TUMBLE.delay);
      }, delay);
    }

    startLoop() {
      if (this.frame) return;

      const tick = (now) => {
        this.frame = null;
        let stillRolling = false;
        this.dice.forEach((die) => {
          stillRolling = die.update(now) || stillRolling;
        });

        if (stillRolling) {
          this.frame = window.requestAnimationFrame(tick);
        }
      };

      this.frame = window.requestAnimationFrame(tick);
    }

    watchVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.reveal();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.reveal();
          } else {
            this.conceal();
          }
        });
      }, {
        rootMargin: '-8% 0px -12% 0px',
        threshold: 0.24,
      });

      this.observer.observe(this);
    }
  }

  if (!customElements.get('player-count-shop')) {
    customElements.define('player-count-shop', PlayerCountShop);
  }
})();
